import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { useState } from "react";
import { api } from "../api";

const ERRORS: Record<string, string> = {
  not_invited: "That account hasn't been invited to the Owners Module. Ask your asset manager or the Help Desk for access.",
  expired: "The sign-in took too long. Please try again.",
  failed: "Sign-in didn't complete. Please try again.",
  cancelled: "Sign-in was cancelled.",
};

/** Same seven sample logins as the canvas — shown only when the API runs with DEV_LOGIN=on. */
const SAMPLES = [
  ["Admin", "Seth Oliver", "seth.oliver@example.com"],
  ["Asset Manager", "Chris Halvorsen", "chris.halvorsen@example.com"],
  ["Owner", "Mark Hollis", "mark.hollis@example.com"],
  ["Owners Investor", "Priya Nair", "priya.nair@example.com"],
  ["HMA User", "Dana Kessler", "dana.kessler@example.com"],
  ["Special Servicer", "Raj Patel", "raj.patel@example.com"],
  ["Receiver", "Laura Brandt", "laura.brandt@example.com"],
] as const;

export function SignIn() {
  const [params] = useSearchParams();
  const qc = useQueryClient();
  const [err, setErr] = useState<string | null>(null);
  const providers = useQuery({
    queryKey: ["providers"],
    queryFn: () => api<{ providers: { key: string; label: string }[]; devLogin: boolean }>("/api/auth/providers"),
  });
  const error = err ?? (params.get("error") ? ERRORS[params.get("error")!] ?? ERRORS.failed : null);

  async function devLogin(email: string) {
    try {
      await api("/api/auth/dev-login", { method: "POST", body: { email } });
      await qc.invalidateQueries({ queryKey: ["me"] });
    } catch (e: any) {
      setErr(e.message);
    }
  }

  return (
    <div className="signin">
      <div className="box">
        <img className="logo-img" src="/fyrehorse-logo.png" alt="Fyrehorse by Oliver Hotels & Resorts" width={360} height={225} />
        {error && <div className="err" role="alert">{error}</div>}
        {/* Passwords and MFA are handled by the identity provider (PROPOSAL §5, decision D1). */}
        {providers.data?.providers.map((p) => (
          <a key={p.key} className="btn gold" href={`/api/auth/login/${p.key}`} style={{ display: "block", textDecoration: "none" }}>
            {p.label}
          </a>
        ))}
        {providers.data && providers.data.providers.length === 0 && !providers.data.devLogin && (
          <p className="muted">Sign-in isn't configured yet.</p>
        )}
        {providers.data?.devLogin && (
          <div className="samples">
            <div className="dev-bar">SAMPLE LOGINS · DEV ONLY · NOT IN FINAL PRODUCT</div>
            {SAMPLES.map(([level, name, email]) => (
              <button key={email} onClick={() => devLogin(email)}>
                <strong>{level}</strong> · {name}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
